import type { Location } from 'history';
import { useCallback, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Route, Switch, useHistory } from 'react-router';
import ObjectiveModal from '../../react/features/Objectives/ObjectiveModal';
import ReviewViewModal from '../../react/features/Reviews/components/View/Modal';
import useLocation, { BackgroundRouteContextProvider, useModalRouteDepth } from '../../react/hooks/useLocation';
import { OBJECTIVE_SINGLE, REVIEW_VIEW } from '../../routes';
import ErrorBoundary from '../ErrorBoundary';
import RouteError from '../RouteError';

export default function ModalRoutes() {
    return (
        <BackgroundRouteContextProvider value={false}>
            <ErrorBoundary errorComponent={RouteError}>
                <ModalRoutesInner />
            </ErrorBoundary>
        </BackgroundRouteContextProvider>
    );
}

function ModalRoutesInner() {
    const { showRoutes, isOpen, location, onClose, onAfterClose } = useModalRouteState();

    if (!showRoutes) {
        return null;
    }

    return (
        <Switch location={location}>
            <Route
                exact
                path={OBJECTIVE_SINGLE}
                render={({ match }) => (
                    <ObjectiveModal
                        // We're using key because the underlying angular component can't handle
                        // changing objectives and withData can return stale results
                        key={match.params.objectiveId}
                        isOpen={isOpen}
                        onClose={onClose}
                        objectiveId={match.params.objectiveId}
                        cycleId={match.params.cycleId}
                        onAfterClose={onAfterClose}
                        onRemove={onClose}
                    />
                )}
            />
            <Route
                exact
                path={REVIEW_VIEW}
                render={({ match }) => (
                    <ReviewViewModal
                        reviewId={match.params.reviewId}
                        isOpen={isOpen}
                        onClose={onClose}
                        onAfterClose={onAfterClose}
                    />
                )}
            />
        </Switch>
    );
}

const useModalRouteState = () => {
    const history = useHistory();
    const modalRouteDepth = useModalRouteDepth();

    const location = useLocation();
    const prevLocation = useRef<Location>();
    const [lastClosedLocation, setLastClosedLocation] = useState<Location>();
    const locationChanged = location !== prevLocation.current;
    const lastLocationWasClosed = lastClosedLocation === prevLocation.current;

    const locationToRender =
        prevLocation.current && locationChanged && !lastLocationWasClosed ? prevLocation.current : location;

    const isOpen = location === locationToRender;
    const showRoutes = modalRouteDepth || !isOpen;

    useEffect(() => {
        if (modalRouteDepth) {
            prevLocation.current = location;
        }
    }, [location, modalRouteDepth]);

    const onClose = useCallback(() => history.go((modalRouteDepth ?? 1) * -1), [history, modalRouteDepth]);
    const onAfterClose = () => setLastClosedLocation(locationToRender);

    return { showRoutes, isOpen, location: locationToRender, onClose, onAfterClose };
};
